/**
 * PwaUpdateBanner.jsx — brief, subtle PWA update indicator.
 *
 * Updates are FULLY AUTOMATIC (see PwaUpdateContext). This banner is only ever
 * shown for a moment:
 *   • reloading: informational "Laddar om för senaste version" (no button) —
 *     shown briefly right before the automatic reload.
 *   • fallback:  technical backup only — adds a "Ladda om" button if the
 *     automatic reload could not complete.
 *
 * Compact dark/gold pill, anchored above the mobile bottom nav, never blocks
 * the app, respects safe areas.
 */
import React from 'react';
import { RefreshCw } from 'lucide-react';
import { usePwaUpdate } from '../context/PwaUpdateContext';

function BannerShell({ children }) {
  return (
    <div
      data-testid="pwa-update-banner"
      className="fixed left-1/2 -translate-x-1/2 z-[120] w-[calc(100%-2rem)] max-w-md px-1"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-3 rounded-2xl border px-3.5 py-2.5 shadow-2xl"
        style={{
          background: "rgb(var(--tn-bg-12141c) / 0.94)",
          borderColor: 'rgba(212, 167, 74, 0.35)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function PwaUpdateBanner() {
  const pwa = usePwaUpdate();
  if (!pwa) return null;

  // Primary flow: brief informational banner shown right before auto-reload.
  if (pwa.showReloadingBanner) {
    return (
      <BannerShell>
        <RefreshCw className="w-4 h-4 shrink-0 animate-spin" style={{ color: "var(--tn-readable-d4a74a, rgb(var(--tn-text-d4a74a)))" }} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[rgb(var(--tn-text-ffffff))] leading-tight truncate">
            Ny version tillgänglig
          </p>
          <p className="text-[11px] text-[rgb(var(--tn-text-ffffff))]/55 leading-tight truncate">
            Laddar om för senaste version
          </p>
        </div>
      </BannerShell>
    );
  }

  // Technical backup only.
  if (pwa.showFallbackBanner) {
    return (
      <BannerShell>
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping"
            style={{ backgroundColor: '#D4A74A' }}
          />
          <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: '#D4A74A' }} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[rgb(var(--tn-text-ffffff))] leading-tight truncate">
            Ny version tillgänglig
          </p>
          <p className="text-[11px] text-[rgb(var(--tn-text-ffffff))]/55 leading-tight truncate">
            Laddar om för senaste version
          </p>
        </div>
        <button
          type="button"
          onClick={pwa.applyUpdate}
          data-testid="pwa-update-reload-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12.5px] font-semibold transition active:scale-[0.97] hover:brightness-110"
          style={{ backgroundColor: '#D4A74A', color: '#1A1408' }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Ladda om
        </button>
      </BannerShell>
    );
  }

  return null;
}
